import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { request } from "../../../utils/api";
import { toast } from "react-toastify";
import ChatLongPolling from "./ChatLongPolling";

const AddOrder = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    client_id: "",
    service_id: "",
    start_date: "",
    end_date: "",
    price: "",
  });

  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getClients = async () => {
    const response = await request({ url: "clients", method: "GET" });
    return response.data || [];
  };

  const getServices = async () => {
    const response = await request({ url: "services", method: "GET" });
    return response.data || [];
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingDropdowns(true);
        const [clientsData, servicesData] = await Promise.all([getClients(), getServices()]);
        const transformedClients = transformDataForDropDown(clientsData.items, "name");
        const transformedServices = transformDataForDropDown(servicesData.items, "title");
        setClients(transformedClients);
        setServices(transformedServices);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingDropdowns(false);
      }
    };
    fetchData();
  }, []);


  const transformDataForDropDown = (data = [], labelKey = "name") => {
    return data.map((item) => ({
      value: item.id,
      label: item[labelKey] || item.name,
    }));
  };

  const makeData = () => {
    const data = new FormData();
    data.append("client_id", formData.client_id);
    data.append("service_id", formData.service_id);
    data.append("project_start_date", formData.start_date);
    data.append("project_end_date", formData.end_date);
    data.append("project_price", formData.price);
    data.append("status", "Pending");
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    try {
      const data = makeData();
      await request({
        url: "orders",
        method: "POST",
        data,
      });
      toast.success("Order added successfully!");
      navigate("/order-list");
    } catch (err) {
      toast.error("Failed to add order");
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/orders">Orders</Link>
            </li>
            <li className="breadcrumb-item active">Add Order</li>
          </ol>
        </div>
      </div>

      {/* Page Body */}
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1">
        <div className="container-fluid">
          <div className="card shadow-sm">
            <div className="card-header">
              <h6 className="card-title m-0">Add New Order</h6>
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Client */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Select Client</label>
                    <select
                      className="form-select"
                      name="client_id"
                      value={formData.client_id}
                      onChange={handleChange}
                      disabled={loadingDropdowns}
                      required
                    >
                      {loadingDropdowns ? (
                        <option>Loading clients...</option>
                      ) : (
                        <>
                          <option value="">-- Select Client --</option>
                          {clients.map((client) => (
                            <option key={client.value} value={client.value}>
                              {client.label}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>

                  {/* Service */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Select Service</label>
                    <select
                      className="form-select"
                      name="service_id"
                      value={formData.service_id}
                      onChange={handleChange}
                      disabled={loadingDropdowns}
                      required
                    >
                      {loadingDropdowns ? (
                        <option>Loading services...</option>
                      ) : (
                        <>
                          <option value="">-- Select Service --</option>
                          {services.map((service) => (
                            <option key={service.value} value={service.value}>
                              {service.label}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>

                  {/* Start Date */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Project Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* End Date */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Project End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Price */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Project Price</label>
                    <input
                      type="number"
                      className="form-control"
                      name="price"
                      placeholder="Enter project price"
                      value={formData.price}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" disabled={loadingSubmit}>
                    {loadingSubmit ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      "Save Order"
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOrder;